import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const FloatingActionButtons = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDashboardToast, setShowDashboardToast] = useState(false);
  const [dashboardToastDismissed, setDashboardToastDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);
      setIsVisible(position > 300); // Show after scrolling 300px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show dashboard toast for authenticated users after a delay
  useEffect(() => {
    if (user && !dashboardToastDismissed) {
      const timer = setTimeout(() => {
        setShowDashboardToast(true);
        // Auto-hide after 8 seconds
        setTimeout(() => {
          setShowDashboardToast(false);
        }, 8000);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [user, dashboardToastDismissed]);

  // Detect when modals are open
  useEffect(() => {
    const checkForModals = () => {
      // Look for modal overlay elements that typically have these classes/attributes
      const modalSelectors = [
        '[role="dialog"]',
        '.modal-overlay',
        '.fixed.inset-0.bg-black',
        '.fixed.inset-0.bg-gray-600',
        '.ReactModal__Overlay',
        // Common modal patterns
        'div[class*="fixed"][class*="inset-0"][class*="bg-black"]',
        'div[class*="fixed"][class*="inset-0"][class*="bg-gray"]'
      ];
      
      const hasOpenModal = modalSelectors.some(selector => {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).some(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        });
      });
      
      setIsModalOpen(hasOpenModal);
    };

    // Check immediately
    checkForModals();
    
    // Set up observer for DOM changes
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleWhatsAppChat = () => {
    const phoneNumber = '254793863320';
    const message = encodeURIComponent('Hello! I need academic writing help. Can you assist me?');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };


  const handleDashboardNavigation = () => {
    // Determine dashboard route based on user role
    let dashboardRoute = '/client/dashboard';
    
    if (profile?.is_admin) {
      dashboardRoute = '/admin/dashboard';
    } else if (profile?.is_writer) {
      dashboardRoute = '/writer/dashboard';
    } else if (profile?.is_qa) {
      dashboardRoute = '/qa/dashboard';
    }
    
    navigate(dashboardRoute);
  };

  const handleDismissToast = () => {
    setShowDashboardToast(false);
    setDashboardToastDismissed(true);
  };

  // Don't render if modal is open
  if (isModalOpen) {
    return null;
  }

  return (
    <div className="floating-action-buttons fixed right-4 bottom-20 md:bottom-8 z-150 flex flex-col space-y-3">
      {/* Dashboard Toast */}
      <AnimatePresence>
        {showDashboardToast && user && (
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            transition={{ duration: 0.3, type: "spring" }}
            className="absolute bottom-16 right-0 mb-4 mr-2 max-w-sm"
          >
            <div className="bg-gradient-to-r from-primary/90 to-secondary/90 backdrop-blur-xl p-5 rounded-xl shadow-2xl border border-white/20">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <Icon name="BarChart3" size={16} className="text-white mr-2" />
                    <h4 className="text-white font-medium text-sm">Quick Access</h4>
                  </div>
                  <p className="text-white/90 text-sm mb-3 leading-relaxed">
                    Track your assignments and classes
                  </p>
                  <button
                    onClick={handleDashboardNavigation}
                    className="w-full bg-white/20 hover:bg-white/30 text-white text-sm py-2 px-4 rounded-lg transition-colors border border-white/20 hover:border-white/30 font-medium"
                  >
                    Go to Dashboard
                  </button>
                </div>
                <button
                  onClick={handleDismissToast}
                  className="text-white/60 hover:text-white/80 transition-colors p-1"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="w-12 h-12 rounded-full backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/30 hover:border-white/40 shadow-lg hover:shadow-xl flex items-center justify-center text-white transition-all duration-300"
            aria-label="Scroll to top"
          >
            <Icon name="ArrowUp" size={20} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Dashboard Button - Only show for authenticated users */}
      {user && (
        <AnimatePresence>
          <motion.button
            initial={{ opacity: 0, scale: 0.8, x: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              x: 0,
              rotate: [0, -3, 3, 0]
            }}
            transition={{ 
              duration: 0.4, 
              delay: 0.3,
              rotate: {
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4
              }
            }}
            whileHover={{ 
              scale: 1.1,
              boxShadow: "0 10px 25px rgba(59, 130, 246, 0.4)",
              y: -5
            }}
            whileTap={{ 
              scale: 0.95,
              transition: { duration: 0.1 }
            }}
            onClick={handleDashboardNavigation}
            className="relative w-12 h-12 md:w-14 md:h-14 rounded-full backdrop-blur-xl bg-gradient-to-br from-blue-500/80 to-indigo-600/80 hover:from-indigo-600/90 hover:to-blue-500/90 border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl flex items-center justify-center text-white transition-all duration-300 overflow-hidden group"
            aria-label="Go to Dashboard"
          >
            <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
            <Icon name="BarChart3" size={20} className="relative z-10 text-white" />
            <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
            </div>
          </motion.button>
        </AnimatePresence>
      )}
      

      {/* WhatsApp Button */}
      <AnimatePresence>
        <motion.button
        initial={{ opacity: 0, scale: 0.8, x: 20 }}
        animate={{ 
          opacity: 1, 
          scale: 1, 
          x: 0,
          y: [0, -2, 0]
        }}
        transition={{ 
          duration: 0.4, 
          delay: 0.1,
          y: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }
        }}
        whileHover={{ 
          scale: 1.1,
          boxShadow: "0 10px 25px rgba(37, 211, 102, 0.4)",
          y: -5
        }}
        whileTap={{ 
          scale: 0.95,
          transition: { duration: 0.1 }
        }}
        onClick={handleWhatsAppChat}
        className="relative w-14 h-14 md:w-16 md:h-16 rounded-full backdrop-blur-xl bg-gradient-to-br from-[#25D366]/80 to-[#128C7E]/80 hover:from-[#128C7E]/90 hover:to-[#25D366]/90 border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl flex items-center justify-center text-white transition-all duration-300 overflow-hidden group"
        aria-label="Chat on WhatsApp"
      >
        <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
        <WhatsAppIcon size={28} className="relative z-10 text-white" />
        <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
        </div>
        {/* Pulse ring animation */}
        <div className="absolute inset-0 rounded-full border-2 border-[#25D366] opacity-75 animate-ping"></div>
      </motion.button>
      </AnimatePresence>
    </div>
  );
};

export default FloatingActionButtons;